<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';

import badgeShieldCheckIcon from '@clovyr/bed/assets/images/icons/ai-landing/badge-shield-check.svg';
import eyeOffIcon from '@clovyr/bed/assets/images/icons/ai-landing/eye-off.svg';
import keyRoundIcon from '@clovyr/bed/assets/images/icons/ai-landing/key-round.svg';
import serverIcon from '@clovyr/bed/assets/images/icons/ai-landing/server.svg';
import BaseSvg from '@clovyr/bed/components/icons/BaseSvg.vue';

import privacyImage from '../../../assets/images/ai-landing/landing-md.jpg';

import ImageWithFallback from './ImageWithFallback.vue';

type Point = {
  iconSrc: string;
  title: string;
  description: string;
};

const points: Point[] = [
  {
    iconSrc: serverIcon,
    title: 'Runs on your infrastructure',
    description:
      "Every compute resource runs on Clovyr's infrastructure, isolated to your account. Clovyr never reads your data or conversations.",
  },
  {
    iconSrc: eyeOffIcon,
    title: 'No telemetry. No tracking.',
    description:
      'Zero usage data sent back to us. What your team discusses stays entirely within your environment.',
  },
  {
    iconSrc: keyRoundIcon,
    title: 'You hold the keys',
    description:
      'Bring your own encryption keys and security configuration. Your security posture, your rules.',
  },
];

const visualVisible = ref(false);
const copyVisible = ref(false);
const visiblePoints = ref<boolean[]>(points.map(() => false));

const sectionRef = ref<HTMLElement | null>(null);

let observer: IntersectionObserver | undefined;

onMounted(() => {
  const visualNode = sectionRef.value?.querySelector<HTMLElement>('.ai-privacy__visual') ?? null;
  const copyNode = sectionRef.value?.querySelector<HTMLElement>('.ai-privacy__copy-column') ?? null;

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        if (entry.target === visualNode) {
          visualVisible.value = true;
        }

        if (entry.target === copyNode) {
          copyVisible.value = true;
        }

        const pointIndex = Number((entry.target as HTMLElement).dataset.index);
        if (!Number.isNaN(pointIndex)) {
          visiblePoints.value[pointIndex] = true;
        }

        observer?.unobserve(entry.target);
      });
    },
    {
      threshold: 0.12,
    },
  );

  [visualNode, copyNode].forEach((node) => {
    if (node instanceof Element) {
      observer?.observe(node);
    }
  });

  copyNode?.querySelectorAll<HTMLElement>('.ai-privacy__point').forEach((node) => {
    observer?.observe(node);
  });
});

onBeforeUnmount(() => {
  observer?.disconnect();
});
</script>

<template>
  <section ref="sectionRef" class="ai-privacy">
    <div class="ai-privacy__inner">
      <div class="ai-privacy__visual" :class="{ 'is-visible': visualVisible }">
        <div class="ai-privacy__visual-glow"></div>

        <div class="ai-privacy__card">
          <ImageWithFallback
            :src="privacyImage"
            alt=""
            aria-hidden="true"
            class="ai-privacy__card-photo"
          />

          <div class="ai-privacy__badge-wrap">
            <div class="ai-privacy__badge-core">
              <BaseSvg
                class="ai-privacy__badge-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
                role="img"
              >
                <image
                  :href="badgeShieldCheckIcon"
                  x="0"
                  y="0"
                  width="100%"
                  height="100%"
                  preserveAspectRatio="xMidYMid meet"
                />
              </BaseSvg>
            </div>

            <div class="ai-privacy__badge-ring ai-privacy__badge-ring--one"></div>
            <div class="ai-privacy__badge-ring ai-privacy__badge-ring--two"></div>
            <div class="ai-privacy__badge-ring ai-privacy__badge-ring--three"></div>
          </div>

          <div class="ai-privacy__card-copy">
            <div class="ai-privacy__card-title">Your data stays yours.</div>
            <p class="ai-privacy__card-text">
              Unlike SaaS AI tools that process your data on shared infrastructure, Clovyr AI Cloud
              is isolated inside your own dedicated environment from day one.
            </p>
          </div>

          <div class="ai-privacy__diagram">
            <div class="ai-privacy__diagram-label">Your Clovyr Environment</div>

            <div class="ai-privacy__diagram-row">
              <div class="ai-privacy__diagram-box ai-privacy__diagram-box--green">
                <div class="ai-privacy__diagram-box-title">Clovyr AI</div>
                <div class="ai-privacy__diagram-box-subtitle">Compute</div>
              </div>

              <div class="ai-privacy__diagram-link">↔</div>

              <div class="ai-privacy__diagram-box ai-privacy__diagram-box--violet">
                <div class="ai-privacy__diagram-box-title ai-privacy__diagram-box-title--violet">
                  Vector DB
                </div>
                <div class="ai-privacy__diagram-box-subtitle">Storage</div>
              </div>

              <div class="ai-privacy__diagram-link">↔</div>

              <div class="ai-privacy__diagram-box ai-privacy__diagram-box--blue">
                <div class="ai-privacy__diagram-box-title ai-privacy__diagram-box-title--blue">
                  Your Docs
                </div>
                <div class="ai-privacy__diagram-box-subtitle">File Store</div>
              </div>
            </div>

            <div class="ai-privacy__diagram-foot">
              <span class="ai-privacy__diagram-pulse"></span>
              <span class="ai-privacy__diagram-foot-text">
                All traffic stays within your private environment
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="ai-privacy__copy-column" :class="{ 'is-visible': copyVisible }">
        <p>Privacy by design</p>
        <h2>AI that never <span>phones home.</span></h2>
        <p class="ai-privacy__copy">
          Enterprise AI tools often require you to trust their cloud with your most sensitive data.
          Clovyr flips the model: your infrastructure, your models, your conversations.
        </p>

        <article
          v-for="(point, idx) in points"
          :key="point.title"
          class="ai-privacy__point"
          :class="{ 'is-visible': visiblePoints[idx] }"
          :style="{ '--delay': `${idx * 100}ms` }"
          :data-index="idx"
        >
          <div class="ai-privacy__point-icon">
            <BaseSvg
              class="ai-privacy__point-icon-svg"
              viewBox="0 0 24 24"
              aria-hidden="true"
              role="img"
            >
              <image
                :href="point.iconSrc"
                x="0"
                y="0"
                width="100%"
                height="100%"
                preserveAspectRatio="xMidYMid meet"
              />
            </BaseSvg>
          </div>

          <div>
            <div class="ai-privacy__point-title">{{ point.title }}</div>
            <div class="ai-privacy__point-text">{{ point.description }}</div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use '../../styles/components/ai-landing-shared' as shared;

.ai-privacy {
  position: relative;
  overflow: hidden;
  background: #221a33;
}

.ai-privacy__inner {
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 5rem;
  padding: 7rem 0;
}

.ai-privacy__visual {
  position: relative;
  flex: 1;
  opacity: 0;
  transform: translateX(-40px);
}

.ai-privacy__visual.is-visible {
  animation: slideInLeft 0.7s ease forwards;
}

.ai-privacy__visual-glow {
  position: absolute;
  inset: 0;
  border-radius: 1.5rem;
  background: rgba(0, 191, 116, 0.05);
  filter: blur(48px);
}

.ai-privacy__card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  border: 1px solid rgba(0, 191, 116, 0.15);
  border-radius: 1.5rem;
  background: #2a1f40;
  padding: 2.5rem;
  overflow: hidden;
  text-align: center;
}

.ai-privacy__card-photo {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.06;
  pointer-events: none;
}

.ai-privacy__badge-wrap {
  position: relative;
  z-index: 1;
}

.ai-privacy__badge-core {
  display: flex;
  width: 8rem;
  height: 8rem;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(0, 191, 116, 0.08);
  border: 1px solid rgba(0, 191, 116, 0.2);
}

.ai-privacy__badge-icon {
  width: 3.5rem;
  height: 3.5rem;
  color: #00bf74;
}

.ai-privacy__badge-ring {
  position: absolute;
  inset: 0;
  border: 1px solid rgba(0, 191, 116, 0.1);
  border-radius: 999px;
  animation: pulseRing 2.5s ease-out infinite;
}

.ai-privacy__badge-ring--two {
  animation-delay: 0.7s;
}

.ai-privacy__badge-ring--three {
  animation-delay: 1.4s;
}

.ai-privacy__card-copy {
  position: relative;
  z-index: 1;
}

.ai-privacy__card-title {
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.ai-privacy__card-text {
  margin: 0;
  max-width: 300px;
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.625;
}

.ai-privacy__diagram {
  position: relative;
  z-index: 1;
  width: 100%;
  background: #1b132a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.9rem;
  padding: 1.25rem;
  text-align: left;
}

.ai-privacy__diagram-label {
  margin-bottom: 0.75rem;
  color: #4b5563;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.ai-privacy__diagram-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ai-privacy__diagram-box {
  flex: 1;
  border-radius: 0.625rem;
  background: #221a33;
  padding: 0.625rem;
  text-align: center;
}

.ai-privacy__diagram-box--green {
  border: 1px solid rgba(0, 191, 116, 0.2);
}

.ai-privacy__diagram-box--violet {
  border: 1px solid rgba(168, 85, 247, 0.2);
}

.ai-privacy__diagram-box--blue {
  border: 1px solid rgba(59, 130, 246, 0.2);
}

.ai-privacy__diagram-box-title {
  color: #00bf74;
  font-size: 0.75rem;
  font-weight: 500;
}

.ai-privacy__diagram-box-title--violet {
  color: #c084fc;
}

.ai-privacy__diagram-box-title--blue {
  color: #60a5fa;
}

.ai-privacy__diagram-box-subtitle {
  color: #4b5563;
  font-size: 10px;
}

.ai-privacy__diagram-link {
  color: #374151;
  font-size: 0.75rem;
}

.ai-privacy__diagram-foot {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.ai-privacy__diagram-pulse {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: #00bf74;
  animation: simplePulse 1.7s ease-in-out infinite;
}

.ai-privacy__diagram-foot-text {
  color: #4b5563;
  font-size: 10px;
}

.ai-privacy__copy-column {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 1.5rem;
  opacity: 0;
  transform: translateY(20px);
}

.ai-privacy__copy-column.is-visible {
  animation: fadeLift 0.55s ease forwards;
}

.ai-privacy__copy-column > p:first-child {
  @include shared.eyebrow;
}

.ai-privacy__copy-column h2 {
  @include shared.section-title;

  margin: 0;
}

.ai-privacy__copy-column h2 span {
  @include shared.accent-span;
}

.ai-privacy__copy {
  margin-bottom: 1rem;
  @include shared.section-copy-text;
}

.ai-privacy__point {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  background: #2a1f40;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 1rem;
  padding: 1.25rem;
  opacity: 0;
  transform: translateX(30px);
}

.ai-privacy__point.is-visible {
  animation: slideInRight 0.45s ease var(--delay, 0ms) forwards;
}

.ai-privacy__point-icon {
  display: flex;
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 0.8rem;
  border: 1px solid rgba(0, 191, 116, 0.2);
  background: rgba(0, 191, 116, 0.1);
}

.ai-privacy__point-icon-svg {
  width: 1.125rem;
  height: 1.125rem;
  color: #00bf74;
}

.ai-privacy__point-title {
  color: #fff;
  font-size: 0.9rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.ai-privacy__point-text {
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.625;
}

@media (min-width: 1024px) {
  .ai-privacy__inner {
    flex-direction: row;
    align-items: center;
    gap: 5rem;
  }

  .ai-privacy__copy-column {
    gap: 1rem;
  }
}

@media (max-width: 1023px) {
  .ai-privacy__inner {
    padding: 6rem 2rem;
    gap: 4rem;
  }

  .ai-privacy__copy-column {
    max-width: 52rem;
    margin: 0 auto;
  }
}

@media (max-width: 800px) {
  .ai-privacy__inner {
    padding: 5.5rem 1.5rem;
    gap: 3rem;
  }

  .ai-privacy__card {
    padding: 1.75rem;
  }

  .ai-privacy__diagram-row {
    gap: 0.45rem;
  }
}

@media (max-width: 640px) {
  .ai-privacy__inner {
    padding: 5rem 1rem;
  }

  .ai-privacy__card {
    padding: 1.25rem;
  }

  .ai-privacy__badge-core {
    width: 6.6rem;
    height: 6.6rem;
  }

  .ai-privacy__badge-icon {
    width: 2.8rem;
    height: 2.8rem;
  }

  .ai-privacy__diagram {
    padding: 0.9rem;
  }

  .ai-privacy__diagram-row {
    flex-direction: column;
    align-items: stretch;
  }

  .ai-privacy__diagram-link {
    text-align: center;
    transform: rotate(90deg);
  }

  .ai-privacy__point {
    padding: 1rem;
  }

  .ai-privacy__point-text {
    font-size: 0.82rem;
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@include shared.keyframes-fade-lift(fadeLift, 20px);

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes pulseRing {
  0% {
    transform: scale(1);
    opacity: 0.5;
  }
  100% {
    transform: scale(2.4);
    opacity: 0;
  }
}

@keyframes simplePulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
}
</style>
